import React from 'react';

// SHOP-68: unlike ConfirmModal.js's generic Confirm/Cancel pair, this
// presents exactly two specific products to choose between (a watched item
// vs. its cheaper equivalent) - "Cancel" wouldn't make sense here, both
// buttons are affirmative actions that differ only in which item they add.
//
// SHOP-131: the main add-to-cart flow additionally passes onChooseBoth,
// which switches this into a 3-option layout (equivalent / both / original,
// each with an explicit action label) without changing WatchedItemsModal's
// existing 2-button usage, which never passes it.
const overlayStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 5000,
};

const modalStyle = {
  backgroundColor: '#fff',
  borderRadius: '12px',
  padding: '20px',
  width: '90%',
  maxWidth: '400px',
  boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
};

const optionButtonStyle = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '12px 14px',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  background: '#f9fafb',
  cursor: 'pointer',
  marginTop: '10px',
};

const optionNameStyle = { fontWeight: 600 };
const optionDetailStyle = { fontSize: '0.85em', color: '#6b7280', marginTop: '2px' };
const optionActionLabelStyle = { fontSize: '0.75em', color: '#2563eb', fontWeight: 700, marginBottom: '4px' };
// SHOP-134: same unit-price accent color used for this badge in
// productscategories.js's item cards - see feedback_ui_color_reuse memory.
const optionUnitPriceStyle = { fontSize: '0.85em', color: '#ff5722', fontWeight: 700, marginTop: '2px' };

// SHOP-134: detail/unit/unit_price for a single option, following the same
// display convention productscategories.js uses for full item cards - a
// detail line, unit shown inline with the store, and a unit_price badge
// gated on it being present and different from price (so items not sold
// by weight/volume don't show a redundant duplicate price). Extracted
// since this exact block repeats for every item option below.
function OptionMeta({ item, translations, formatCurrency }) {
  return (
    <>
      {item.detail && <div style={optionDetailStyle}>{item.detail}</div>}
      <div style={optionDetailStyle}>
        {item.store}
        {item.unit ? ` · ${item.unit}` : ''} · {formatCurrency(item.price)}
      </div>
      {item.unit_price != null && item.unit_price !== item.price && (
        <div style={optionUnitPriceStyle}>
          {formatCurrency(item.unit_price)} / {translations?.unit || 'unit'}
        </div>
      )}
    </>
  );
}

function ChooseCartItemModal({
  isOpen,
  title,
  originalItem,
  equivalentItem,
  onChoose,
  onChooseBoth,
  onCancel,
  translations,
  formatCurrency,
}) {
  if (!isOpen) return null;

  const showBothOption = typeof onChooseBoth === 'function';

  // stopPropagation on the overlay itself, not just the card: this modal is
  // rendered nested inside WatchedItemsModal's own overlay (position: fixed
  // means it still stacks correctly on top), so without this a backdrop
  // click here would bubble up and also fire WatchedItemsModal's onClose.
  return (
    <div
      data-testid="choose-cart-item-overlay"
      style={overlayStyle}
      onClick={(e) => { e.stopPropagation(); onCancel(); }}
    >
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>
          {title || translations?.chooseCartItemTitle || 'Which one do you want to add?'}
        </h3>

        {showBothOption && (
          <button type="button" style={optionButtonStyle} onClick={() => onChoose(equivalentItem)}>
            <div style={optionActionLabelStyle}>
              {translations?.addEquivalentItemLabel || 'Add equivalent item'}
            </div>
            <div style={optionNameStyle}>{equivalentItem.name}</div>
            <OptionMeta item={equivalentItem} translations={translations} formatCurrency={formatCurrency} />
          </button>
        )}

        {showBothOption && (
          <button type="button" style={optionButtonStyle} onClick={onChooseBoth}>
            <div style={optionActionLabelStyle}>
              {translations?.addBothItemsLabel || 'Add both items'}
            </div>
            <div style={optionDetailStyle}>
              {originalItem.name} + {equivalentItem.name}
            </div>
          </button>
        )}

        <button type="button" style={optionButtonStyle} onClick={() => onChoose(originalItem)}>
          {showBothOption && (
            <div style={optionActionLabelStyle}>
              {translations?.continueWithOriginalLabel || 'Continue with original item'}
            </div>
          )}
          <div style={optionNameStyle}>{originalItem.name}</div>
          <OptionMeta item={originalItem} translations={translations} formatCurrency={formatCurrency} />
        </button>

        {!showBothOption && (
          <button type="button" style={optionButtonStyle} onClick={() => onChoose(equivalentItem)}>
            <div style={optionNameStyle}>{equivalentItem.name}</div>
            <OptionMeta item={equivalentItem} translations={translations} formatCurrency={formatCurrency} />
          </button>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              background: '#f3f4f6',
              cursor: 'pointer',
            }}
          >
            {translations?.cancel || 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChooseCartItemModal;
